import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, finalize, mergeMap, take } from 'rxjs/operators';

import { AppConstants } from '../../app.constants';
import { LicenseDialogMessagesEnum } from '../enums/toastr-messages/license-dialog-messages.enum';
import { ToastrMessageEnum } from '../enums/toastr-messages/toastr-message.enum';
import { AuthDataInterface } from '../models/view-models/account-models/auth-data.interface';
import { AuthService } from '../services/auth/auth.service';
import { MonitorHub } from '../../shared-module/hubs/monitor.hub';

@Injectable()
export class AuthAndErrorInterceptor implements HttpInterceptor {
  public router: Router;
  public authService: AuthService;
  public monitorHub: MonitorHub

  token: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isRefreshingToken: boolean;

  constructor(
    public inj: Injector,
    private toastrService: ToastrService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {

    this.router = this.inj.get(Router);

    if (req.headers.get('No-Auth') === 'True') {
      return next.handle(req.clone());
    }

    const authData: AuthDataInterface = JSON.parse(localStorage.getItem(AppConstants.AUTH_DATA_NAME));

    if (authData) {
      return this.handleRequest(req, next, authData.userToken);
    }
    else {
      if (!window.location.href.includes('Account') && !window.location.href.includes('Screen')) {
        this.router.navigateByUrl('/Account');
      }
      return of();
    }
  }

  private handleRequest(req: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    const clonedreq = req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + token)
    });

    return next.handle(clonedreq).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
          if (errorResponse.status === 401 && !errorResponse.error.message?.includes(AppConstants.ACCOUNT_CHANGED_ERROR)) {
            return this.handleUnauthorizedError(req, next);
          }
          else if (errorResponse.error instanceof Blob && errorResponse.error.type === AppConstants.APPLICATION_JSON_MIME_TYPE) {
            return from(errorResponse.error.text()).pipe(
              mergeMap(error => {
                errorResponse.error.message = JSON.parse(error)?.Message;
                return this.handleError(errorResponse, errorResponse.status)
              })
            );
          }
          else {
            return this.handleError(errorResponse, errorResponse.status);
          }
        }
      ));
  }

  private handleError(errorResponse: HttpErrorResponse, status: number) {
    switch (status) {
      case (400): {
        this.toastrService.error(errorResponse.error.message, ToastrMessageEnum.ErrorOccuredTitle);
        break;
      }
      case (401): {
        this.logoutUser(errorResponse.error.message);
        break;
      }
      case (403): {
        this.toastrService.error(errorResponse.error.message, ToastrMessageEnum.ForbiddenTitle, {
          toastClass: 'forbidden-error-toastr'
        });
        this.router.navigateByUrl('/Account');
        break;
      }
      case (405): {
        this.toastrService.error(errorResponse.error.message, ToastrMessageEnum.MethodNotAllowedTitle);
        break;
      }
      case (410): {
        this.toastrService.error(errorResponse.error.message, ToastrMessageEnum.AccessToTheResourseIsNotAvailableTitle);
        break;
      }
      case (500): {
        if (errorResponse.error.message === LicenseDialogMessagesEnum.ConnectionToRemoteServerFailed) {
          this.toastrService.error(LicenseDialogMessagesEnum.ConnectionToServerFailed,
            LicenseDialogMessagesEnum.ConnectionToServerFailedTitle);
        } else {
          this.toastrService.error(errorResponse.error.message);
        }
        break;
      }
      case (0): {
        this.toastrService.error(errorResponse.error.message,
          `${ToastrMessageEnum.ErrorOccuredTitle}. ${ToastrMessageEnum.CheckYourConnectionWithServer}`);
        break;
      }
      default: {
        this.toastrService.error(errorResponse.error.message, ToastrMessageEnum.SomeErrorOccuredTitle);
        break;
      }
    }
    return throwError(errorResponse);
  }

  private handleUnauthorizedError(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const authData: AuthDataInterface = JSON.parse(localStorage.getItem(AppConstants.AUTH_DATA_NAME));

    if (this.isRefreshingToken) {
      return this.token
        .pipe(
          filter(token => !!token),
          take(1),
          concatMap(token =>
            this.handleRequest(req, next, token)
          )
        );
    }

    this.authService = this.inj.get(AuthService);
    this.monitorHub = this.inj.get(MonitorHub);

    this.isRefreshingToken = true;
    this.token.next(null);

    return this.authService.refreshToken(authData.refreshToken)
      .pipe(
        concatMap(data => {
          this.token.next(data.access_token);

          if (!this.monitorHub.connection) {
            this.monitorHub.connectAsUser();
          }

          return this.handleRequest(req, next, data.access_token);
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          this.logoutUser(errorResponse.message);
          return of();
        }),
        finalize(() =>
          this.isRefreshingToken = false
        )
      )
  }

  private logoutUser(errorMessage: string) {
    localStorage.removeItem(AppConstants.AUTH_DATA_NAME);
    this.toastrService.error(errorMessage, ToastrMessageEnum.ErrorOccuredTitle);
    this.router.navigateByUrl('/Account');
  }
}
